import { useEffect, useState } from 'react';
import { RiggProvider, useRigg } from './data/RiggProvider.tsx';
import { navigate, useRoute, type Route } from './lib/router.ts';
import type { Queue } from './data/selectors.ts';
import { cn } from './lib/format.ts';
import { Sidebar } from './components/Sidebar.tsx';
import { AgentChat } from './components/AgentChat.tsx';
import { Toaster, TooltipProvider, Button } from './components/ui.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { OverviewScreen } from './screens/Overview.tsx';
import { TasksScreen } from './screens/Tasks.tsx';
import { ReposScreen } from './screens/Repos.tsx';
import { AgentsScreen } from './screens/Agents.tsx';
import { IntakesScreen } from './screens/Intakes.tsx';
import { PeopleScreen } from './screens/People.tsx';
import { SettingsScreen } from './screens/Settings.tsx';
import { ProtocolScreen } from './screens/Protocol.tsx';
import { ApiDocsScreen } from './screens/ApiDocs.tsx';
import { SuggestedScreen } from './screens/Suggested.tsx';

export function App() {
  return (
    <TooltipProvider>
      <Toaster>
        <RiggProvider>
          <Shell />
        </RiggProvider>
      </Toaster>
    </TooltipProvider>
  );
}

/**
 * Screens that manage or describe a single project — a repository list, a
 * settings form, a member roster. None of them has an answer when "All
 * projects" is selected, so a direct link to one (a bookmark, a paste from
 * before switching) is sent back to Tasks rather than left to render with a
 * project of `null`.
 */
const PROJECT_ONLY_ROUTES = new Set<Route['name']>([
  'overview',
  'repos',
  'agents',
  'intakes',
  'people',
  'settings',
]);

function Shell() {
  const { session, projects, project, allProjects, data } = useRigg();
  const route = useRoute();
  const [queue, setQueue] = useState<Queue>('tasks');
  const [navOpen, setNavOpen] = useState(false);

  const redirectHome = allProjects && PROJECT_ONLY_ROUTES.has(route.name);

  // The URL itself is corrected here, as a side effect — the render below
  // already falls back to Tasks for this one frame, so nothing is shown
  // broken while this catches up.
  useEffect(() => {
    if (redirectHome) navigate('/', true);
  }, [redirectHome]);

  if (!session) {
    // The one screen a signed-out visitor can reach: the reporting protocol,
    // which is what a project's own CLAUDE.md points at and what a routine
    // reads before it has ever talked to Rigg. It carries no token — see
    // Protocol.tsx — so there is nothing here a session would be gating.
    if (route.name === 'protocol') return <ProtocolScreen variant={route.variant} standalone />;
    // Same reasoning as /protocol: a script deciding whether to integrate has
    // no key yet to read this with, so the reference has to be reachable
    // before one exists.
    if (route.name === 'api-docs') return <ApiDocsScreen standalone />;
    return <SignIn />;
  }
  if (!project && !allProjects) return <NoProject hasAny={projects.length > 0} />;

  const routeName = redirectHome ? 'tasks' : route.name;

  /*
   * Four columns only where four columns fit.
   *
   * The old track list — 236 + minmax(500…) + minmax(400…) + a 360px panel —
   * needed 1496px before anything could shrink. Under that the grid overflowed
   * and `overflow-hidden` clipped the last column, so on a 1440px laptop the
   * assistant was not narrow, it was gone: no rail, no button, no hint it
   * existed. A layout that silently drops a feature is worse than one that
   * admits it has run out of room.
   *
   * So the minima now fit the breakpoint they belong to — 860 at lg, 1180 at
   * xl, 1396 at 2xl. Below `xl` the assistant undocks rather than vanishing:
   * the same floating button and sheet the phone layout already used, which is
   * why this costs no new UI.
   */
  return (
    <div className="flex h-screen h-dvh min-w-0 flex-col overflow-hidden bg-app lg:grid lg:grid-cols-[220px_minmax(340px,1fr)_minmax(300px,1fr)] xl:grid-cols-[220px_minmax(340px,1fr)_minmax(300px,1fr)_auto] 2xl:grid-cols-[236px_minmax(420px,940px)_minmax(380px,1fr)_auto]">
      {/*
        Below `lg` the sidebar is the only navigation there is, so it cannot
        just disappear — it becomes a drawer, opened from here, closed by the
        same tap that chose something in it. At `lg` and up this bar is not
        rendered at all: the sidebar is already on screen.
      */}
      <MobileTopBar onMenu={() => setNavOpen(true)} />

      <div
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-[272px] max-w-[85vw] flex-col bg-side transition-transform duration-200 ease-out',
          'lg:static lg:z-auto lg:w-auto lg:max-w-none lg:translate-x-0 lg:transition-none',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Sidebar
          route={route}
          queue={queue}
          onQueue={setQueue}
          onNavigate={() => setNavOpen(false)}
        />
      </div>

      {navOpen ? (
        <div
          className="fixed inset-0 z-30 bg-[#0a0c14]/60 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      ) : null}

      {routeName === 'tasks' ? (
        <TasksScreen queue={queue} selectedId={route.name === 'tasks' ? route.taskId : null} />
      ) : routeName === 'suggested' ? (
        <SuggestedScreen selectedId={route.name === 'suggested' ? route.followUpId : null} />
      ) : routeName === 'overview' ? (
        <OverviewScreen />
      ) : routeName === 'repos' ? (
        <ReposScreen />
      ) : routeName === 'agents' ? (
        <AgentsScreen />
      ) : routeName === 'intakes' ? (
        <IntakesScreen />
      ) : routeName === 'people' ? (
        <PeopleScreen />
      ) : routeName === 'settings' ? (
        // Keyed so the form re-reads the saved values when the project changes,
        // and once its settings row arrives — it reads them once, at mount.
        <SettingsScreen key={`${project?.id}:${Boolean(project && data.settings[project.id])}`} />
      ) : routeName === 'api-docs' ? (
        <ApiDocsScreen />
      ) : (
        <ProtocolScreen variant={route.name === 'protocol' ? route.variant : 'task'} />
      )}
      <AgentChat />
    </div>
  );
}

/** The bar that stands in for the sidebar below `lg`, where there is no room for both. */
function MobileTopBar({ onMenu }: { onMenu: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-2.5 border-b border-line bg-side px-3 py-2.5 lg:hidden">
      <button
        onClick={onMenu}
        aria-label="Open menu"
        className="rg-focus grid size-9 shrink-0 cursor-pointer place-items-center rounded-lg text-hi2 hover:bg-navhover"
      >
        <span aria-hidden className="text-[17px] leading-none">
          ☰
        </span>
      </button>
      <span className="size-[20px] shrink-0 rounded-md bg-accent" aria-hidden />
      <span className="text-[15px] font-bold tracking-[-0.01em] text-hi">Rigg</span>
    </div>
  );
}

/**
 * You are signed in but belong to nothing.
 *
 * With invite-by-email this is a real state — somebody clicked a sign-in link
 * before anybody added them — and it deserves an explanation rather than an
 * empty board.
 */
function NoProject({ hasAny }: { hasAny: boolean }) {
  const { me, signOut } = useRigg();
  return (
    <div className="grid min-h-screen min-h-dvh place-items-center bg-side px-6">
      <div className="max-w-md text-center">
        <span className="mx-auto mb-5 block size-6 rounded-md bg-accent" aria-hidden />
        <h1 className="text-[19px] font-[650] tracking-[-0.01em] text-hi">
          {hasAny ? 'No project selected' : 'Nothing here yet'}
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-body">
          {hasAny
            ? 'Pick a project from the sidebar.'
            : `You are signed in as ${me?.email ?? 'yourself'}, but you are not a member of any project yet. Ask an admin to invite that address.`}
        </p>
        <Button className="mt-5" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
